import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Search } from 'lucide-react';

import { Input, PasswordEyeInput } from '@/components/forms';
import { Button, Spinner } from '@/components/ui';
import { useAuthStore } from '@/stores/useAuthStore';
import { ROUTES } from '@/constants/routes';
import { sanitizeEmail } from '@/lib/sanitize';
import { logger } from '@/lib/logger';
import type { LoginRequest, AuthResult } from '@/types/auth';

import styles from './Login.module.css';

export function Login() {
  const navigate = useNavigate();
  const { logIn, isLoading, isLogged } = useAuthStore();
  const getUser = useAuthStore((s) => s.getUser);

  const [formData, setFormData] = useState<LoginRequest>({
    email: '',
    password: '',
  });

  useEffect(() => {
    if (isLogged) {
      const user = getUser();
      if (user?.is_staff) {
        navigate(ROUTES.dashboard);
      } else {
        navigate(ROUTES.pendingApproval);
      }
    }
  }, [isLogged, navigate, getUser]);

  const handleFormDataChange = (key: string, value: string) => {
    setFormData((prevState: LoginRequest) => ({
      ...prevState,
      [key]: value,
    }));
  };

  const validateForm = (formData: LoginRequest) => {
    const { email, password } = formData;

    if (!email || !password) {
      return { isValid: false, message: 'Please fill in all fields' };
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      return { isValid: false, message: 'Please enter a valid email address' };
    }

    return { isValid: true, message: '' };
  };

  const handleFormSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const validation = validateForm(formData);
    if (!validation.isValid) {
      toast.error(validation.message);
      return;
    }

    const sanitizedData: LoginRequest = {
      email: sanitizeEmail(formData.email),
      password: formData.password,
    };

    try {
      const status: AuthResult = await logIn(sanitizedData, () => logger.info('2FA required'));

      if (status === 'success') {
        toast.success('Login successful');
        const user = getUser();
        if (user?.is_staff) {
          navigate(ROUTES.dashboard);
        } else {
          navigate(ROUTES.pendingApproval);
        }
      }

      if (status === 'confirm_email') {
        toast.success('Please confirm your email');
        navigate(ROUTES.verifyEmail);
      }
    } catch (error) {
      toast.error('An error occurred during login');
      logger.error('Login error:', error);
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.card}>
        <div className={styles.logoRow}>
          <div className={styles.logoIcon}>
            <Search size={16} />
          </div>
          <span className={styles.logoText}>Leads Manager</span>
        </div>

        <h1 className={styles.title}>Sign in to your account</h1>

        <form className={styles.form} onSubmit={handleFormSubmit}>
          <div className={styles.field}>
            <label className={styles.label}>Email</label>
            <Input
              name="email"
              value={formData.email}
              setValue={(value) => handleFormDataChange('email', value)}
              placeholder="you@example.com"
              variant="bordered"
            />
          </div>
          <div className={styles.field}>
            <div className={styles.labelRow}>
              <label className={styles.label}>Password</label>
              <button
                type="button"
                className={styles.forgotLink}
                onClick={() => navigate(ROUTES.forgotPassword)}
              >
                Forgot password?
              </button>
            </div>
            <PasswordEyeInput
              name="password"
              value={formData.password}
              setValue={(value) => handleFormDataChange('password', value)}
              placeholder="Enter your password"
              variant="bordered"
            />
          </div>

          <Button variant="primary" size="md" type="submit" disabled={isLoading}>
            {isLoading ? <Spinner variant="secondary" size="sm" /> : 'Sign In'}
          </Button>
        </form>

        <div className={styles.footer}>
          <span>Don't have an account?</span>
          <button className={styles.footerLink} onClick={() => navigate(ROUTES.register)}>
            Sign Up
          </button>
        </div>
      </div>
    </div>
  );
}
